import { useCallback, useMemo, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/shared/stores/auth.store'
import { useNotificationStore } from '@/shared/stores/notification.store'
import { useWebSocket } from '@/shared/hooks/use-websocket'
import { useToast } from '@/shared/components/ui/toast'
import { forceLogout } from '@/shared/hooks/use-auth'
import { authApi } from '@/features/auth/api/auth.api'
import { CHART_ID_OPTIONS_KEY } from '@/features/search/hooks/use-chart-id-search'
import type { NotificationSummary } from '@/shared/stores/notification.store'

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const incrementUnread = useNotificationStore((s) => s.incrementUnread)
  const addNotification = useNotificationStore((s) => s.addNotification)
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const loggedOutRef = useRef(false)

  // Reset logout flag when auth state changes
  useEffect(() => {
    if (isAuthenticated) loggedOutRef.current = false
  }, [isAuthenticated])

  const handleSessionTerminated = useCallback(() => {
    if (loggedOutRef.current) return
    loggedOutRef.current = true
    forceLogout()
  }, [])

  const handleNotificationNew = useCallback((data: unknown) => {
    incrementUnread()
    if (data && typeof data === 'object' && 'id' in data) {
      addNotification(data as NotificationSummary)
      const notif = data as { title?: string; body?: string }
      addToast({
        title: notif.title ?? 'New Notification',
        description: notif.body,
        variant: 'notification',
      })
    }
    queryClient.invalidateQueries({ queryKey: ['notifications'] })
  }, [incrementUnread, addNotification, addToast, queryClient])

  const handleEncounterUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['workspace', 'queue'] })
  }, [queryClient])

  // An access request or grant changed somewhere (any user, any replica). Every
  // /access tab and request page reads under ['access']; grants also change
  // which ID fields search offers. Only queries on screen refetch now.
  const handleAccessChanged = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['access'] })
    queryClient.invalidateQueries({ queryKey: [CHART_ID_OPTIONS_KEY] })
  }, [queryClient])

  const setUser = useAuthStore((s) => s.setUser)
  const handleConfigInvalidated = useCallback(async () => {
    // Server broadcast either changed a user role/permission (scope='rbac')
    // or a client feature entitlement (scope='entitlement'). Refetch /auth/me
    // once — covers both since permissions + features are on the same payload.
    try {
      const fresh = await authApi.me()
      if (fresh?.data) setUser(fresh.data)
    } catch {
      /* ignore — TTL fallback still applies */
    }
    queryClient.invalidateQueries({ queryKey: ['system-admin', 'feature-catalog'] })
    queryClient.invalidateQueries({ queryKey: ['system-admin', 'client-features'] })
  }, [queryClient, setUser])

  // Subscribed channels only. `config.invalidated` is a server-wide broadcast
  // and reaches every socket without a subscription — see ws-server
  // broadcastConfigInvalidated().
  const channels = useMemo(
    () => user ? [`queue:${user.id}`, `notifications:${user.id}`] : [],
    [user?.id],
  )

  const eventHandlers = useMemo(() => ({
    'session.terminated': handleSessionTerminated,
    'notification.new': handleNotificationNew,
    'encounter.status_changed': handleEncounterUpdate,
    'encounter.assigned': handleEncounterUpdate,
    'config.invalidated': handleConfigInvalidated,
    'access.changed': handleAccessChanged,
  }), [handleSessionTerminated, handleNotificationNew, handleEncounterUpdate, handleConfigInvalidated, handleAccessChanged])

  useWebSocket({
    channels,
    enabled: isAuthenticated && !!user,
    onEvent: eventHandlers,
  })

  return <>{children}</>
}
